import { Dialog } from '@angular/cdk/dialog';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { AlertService, LearningTrailsService, NotificationService } from '@app/core';
import { LearningTrailsCollectionService } from '@app/core/services/learning-trails-collection.service';
import { notNull, safeEmpty, selectedEntity } from '@app/shared/utils';
import { SIDENAV_END, SidenavRef } from '@app/shared/modules/template/components/sidenav';
import { TranslateService } from '@ngx-translate/core';
import { addMinutes } from 'date-fns';
import { LocalStorageService } from 'ngx-webstorage';
import {
  BehaviorSubject,
  Observable,
  catchError,
  debounceTime,
  filter,
  of,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs';
import { Collection } from '../../models';
import { SidenavStudioService } from '../../services/sidenav-studio.service';
import { ModalCoursesComponent } from '../modal-courses/modal-courses.component';

import { TrailsService } from '../../services';
import { ModalDuplicateCoursesComponent } from '../modal-duplicate-courses/modal-duplicate-courses.component';
import { ModalEventsComponent } from '../modal-events/modal-events.component';
import { SidenavLearningStudioCategoryComponent } from '../sidenav-learning-studio-category/sidenav-learning-studio-category.component';
import { SidenavLearningStudioCollectionComponent } from '../sidenav-learning-studio-collection/sidenav-learning-studio-collection.component';
import { SidenavLearningStudioShowcaseComponent } from '../sidenav-learning-studio-showcase/sidenav-learning-studio-showcase.component';

export const COLLECTION_KEY = 'trail-studio-collection';
export const COURSE_KEY = 'trail-studio-course';
export const COURSE_DATE_KEY = 'trail-studio-course-date';
@Component({
  selector: 'app-collection-studio',
  templateUrl: './collection-studio.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CollectionStudioComponent implements OnInit, OnDestroy {
  private readonly dialog = inject(Dialog);
  private readonly _cdrf = inject(ChangeDetectorRef);
  private readonly _alertService = inject(AlertService);
  private readonly _translateService = inject(TranslateService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _sidenavStudioService = inject(SidenavStudioService);
  private readonly trailsService = inject(TrailsService);
  private readonly _learningTrailsService = inject(LearningTrailsService);
  private readonly _learningTrailsCollectionService = inject(LearningTrailsCollectionService);
  private readonly _storageService = inject(LocalStorageService);
  private readonly sidenavEnd = inject<Observable<SidenavRef>>(SIDENAV_END);
  @ViewChild('searchInput') public searchInput: ElementRef<HTMLInputElement>;
  @Input() public colIndex!: number;
  @Input({ required: true }) public categories;
  @Input({ required: true }) public category;
  @Input({ required: true }) public products;
  public collection$ = new BehaviorSubject<Collection>(undefined);
  public collectionsSelected$ = new BehaviorSubject<number[]>([]);
  public coursesSelected$ = new BehaviorSubject<number[]>([]);
  public selectedCourseIdMobile: number;
  public searchCtrl = new FormControl();
  public showInput: boolean = false;
  public collectionOpened: boolean = false;
  public titleControl = new FormControl(undefined, [Validators.required, Validators.minLength(3)]);

  @Input({ required: true })
  public set collection(collection: Collection) {
    this.collection$.next(collection);
    this.titleControl.setValue(collection.nome, { emitEvent: false });
  }

  @Input()
  public set collectionsSelected(collectionsSelected: number[]) {
    this.collectionsSelected$.next(collectionsSelected);
  }

  @Input()
  public set coursesSelected(coursesSelected: number[]) {
    this.coursesSelected$.next(coursesSelected);
  }

  public ngOnInit(): void {
    const collection = this.collection$.value;
    this.collectionOpened = this._storageService.retrieve(COLLECTION_KEY) === collection?.id;
    if (this.collectionOpened) {
      const courseId = this._storageService.retrieve(COURSE_KEY);
      const validateTime = addMinutes(new Date(this._storageService.retrieve(COURSE_DATE_KEY)), 10) > new Date();
      if (validateTime && courseId && collection.cursos.some((course) => course.id === courseId)) {
        const courseIndex = collection.cursos.findIndex((course) => course.id === courseId);
        this.onOpenCourses(null, collection.cursos[courseIndex]);
      }
      if (collection.id === null) {
        setTimeout(() => {
          document.getElementById(`${collection.categoria_id}-new-collection-input`).focus();
          document
            .getElementById(`${collection.categoria_id}-new-collection-input`)
            .scrollIntoView({ behavior: 'smooth', block: 'center' });
          this._cdrf.markForCheck();
        }, 0);
      }
    }

    this.titleControl.valueChanges
      .pipe(
        filter(() => this.titleControl.valid),
        debounceTime(500),
        withLatestFrom(this.collection$),
      )
      .subscribe(([value, collection]) => {
        if (!!collection.id) {
          this.editCollection(collection, value);
        } else {
          this.saveCollection(collection, value);
        }
      });
  }

  public ngOnDestroy(): void {
    this.collectionsSelected$.complete();
    this.coursesSelected$.complete();
  }

  public onToggleInput() {
    this.showInput = !this.showInput;
    if (this.showInput) {
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
      }, 0);
    }
  }

  public onOpenCourses(event?: any, course?: any) {
    if (event) {
      this.blockClick(event);
    }
    this.sidenavEnd.pipe(take(1)).subscribe((ref) => {
      ref.close();
    });
    this.dialog
      .open(ModalCoursesComponent, {
        data: { categories: this.categories, collection: this.collection$.value, products: this.products, course },
        width: '80vw',
      })
      .closed.subscribe((result: any) => {
        if (!result?.reset) {
          this._storageService.clear(COURSE_KEY);
        }
      });
    if (course) {
      this._storageService.store(COURSE_KEY, course.id);
      this._storageService.store(COURSE_DATE_KEY, new Date());
    } else {
      this._storageService.clear(COURSE_KEY);
    }
  }

  public onOpenEvents(course: any) {
    return this.dialog
      .open(ModalEventsComponent, {
        width: '95vw',
        height: '95vh',
        data: { course: { ...course, selected: true }, context: this.getUrlUcIvca() },
      })
      .closed.subscribe();
  }

  public onOpenDuplicateCourses(event: Event, course: any) {
    if (event) {
      this.blockClick(event);
    }

    this.dialog
      .open(ModalDuplicateCoursesComponent, {
        data: {
          categories: this.categories,
          selectedCourse: course,
        },
        panelClass: ['w-[90%]', 'md:w-[40vw]', 'xl:w-[22vw]'],
      })
      .closed.subscribe();
  }

  public filtered(id: number, selected: number[]): boolean {
    if (selected.length) {
      return selected.includes(id);
    }
    return true;
  }

  public onSidenavCollection(collection?: any) {
    this.sidenavEnd.pipe(notNull(), take(1)).subscribe((ref) => {
      ref.open({
        componentRef: SidenavLearningStudioCollectionComponent,
        closedAnchor: true,
        opened: true,
        data: collection,
      });
      ref.closed$.pipe(take(1)).subscribe(() => ({}));
    });
  }

  public onDeleteCollection(collectionId: number) {
    if (collectionId === null) {
      this._sidenavStudioService.deleteCollection(collectionId, this.category);
      return;
    }
    this.sidenavEnd.pipe(take(1)).subscribe((ref) => {
      ref.open({
        componentRef: SidenavLearningStudioCategoryComponent,
        closedAnchor: true,
        opened: false,
      });
      ref.closed$.pipe(take(1)).subscribe(() => ({}));
    });
    this._alertService
      .confirm({
        message: this._translateService.instant(`trilhas.estudio.excluir-colecao`),
      })
      .pipe(
        notNull(),
        switchMap(() => this._learningTrailsCollectionService.deleteCollection(collectionId)),
        catchError(() => of({ ret: 0 })),
      )
      .subscribe((res) => {
        this._sidenavStudioService.deleteCollection(collectionId, this.category);
        if (res.ret === 1) {
          this._notificationService.success(this._translateService.instant(`trilhas.estudio.colecao-excluida`));
        } else {
          this._notificationService.error(this._translateService.instant(`trilhas.estudio.erro-excluir-colecao`));
        }
      });
  }

  public onSidenavStudioShowcase(course: any) {
    this.sidenavEnd.pipe(take(1)).subscribe((ref) => {
      ref.open({
        componentRef: SidenavLearningStudioShowcaseComponent,
        closedAnchor: true,
        opened: true,
        panelWidth: '500px',
        mode: 1,
        data: course,
      });
      ref.closed$.pipe(take(1)).subscribe(() => ({}));
    });
  }

  public blockClick(event) {
    event.stopPropagation();
  }

  public onMove(course: any, direction: number) {
    this.reorderCourses(this.collection$.value, course, direction);
  }

  public openCollection($event, collection: Collection): void {
    if ($event) {
      this._storageService.store(COLLECTION_KEY, collection.id);
    } else {
      this._storageService.clear(COLLECTION_KEY);
      this.searchCtrl.reset();
    }
  }

  public onHighlightCollection(collection: Collection) {
    this._learningTrailsCollectionService
      .highlightCollection(collection.id)
      .pipe(safeEmpty(), withLatestFrom(this.collection$))
      .subscribe({
        next: ([res, collection]) => {
          collection.destaque = res.destaque;
          this.collection$.next(collection);
          this._notificationService.success(this._translateService.instant(`trilhas.estudio.highlight.sucesso`));
        },
        error: (e) => {
          this._notificationService.error(this._translateService.instant(`trilhas.estudio.highlight.erro`));
        },
      });
  }

  public onOpenCourseMenu(courseId: number) {
    this.selectedCourseIdMobile = courseId;
  }

  public onCloseCourseMenu(courseId: number) {
    if (this.selectedCourseIdMobile !== courseId) return;
    this.selectedCourseIdMobile = null;
  }

  private getUrlUcIvca(): string {
    return this.trailsService.uc$.value
      ? 'universidade-corporativa'
      : this.trailsService.ivca$.value
        ? 'ivca'
        : 'trilhas-de-aprendizagem';
  }

  private reorderCourses(collection: any, course: any, direction: number) {
    const courses = collection.cursos
      .map((c) => {
        if (c.ordem === course.ordem + direction) {
          return { ...c, ordem: course.ordem };
        }
        if (c.id === course.id) {
          return { ...c, ordem: c.ordem + direction };
        }
        return c;
      })
      .sort((a, b) => a.ordem - b.ordem);

    this._learningTrailsService
      .reorderCourses(
        collection.id,
        courses.map((c) => ({ id: c.id, ordem: c.ordem })),
      )
      .subscribe({
        next: (event: any) => {
          this.collection$.next({
            ...collection,
            cursos: courses,
          });
          this._notificationService.success(this._translateService.instant(`trilhas.estudio.ordenar.sucesso`));
        },
        error: (e) => {
          this._notificationService.error(this._translateService.instant(`trilhas.estudio.ordenar.erro`));
        },
      });
  }

  private editCollection(collection: Collection, title: string) {
    if (collection.entidades) {
      collection.entidades = selectedEntity(collection.entidades);
    }
    this._learningTrailsCollectionService.updateCollection({ ...collection, nome: title }).subscribe((res) => {
      this._sidenavStudioService.editOrSaveCollection({
        id: res.id,
        categoria_id: collection.categoria_id,
        cursos: collection.cursos,
        entidades: collection.entidades,
        nome: title,
        slug: res.slug,
        autor: collection.autor,
        destaque: collection.destaque,
        cpb: collection.cpb,
      });
      this._notificationService.success(this._translateService.instant('geral.sucesso'));
      this._storageService.store(COLLECTION_KEY, res.id);
    });
  }

  private saveCollection(collection: Collection, title: string) {
    this._learningTrailsCollectionService.postCollection({ ...collection, nome: title }).subscribe((res) => {
      this._sidenavStudioService.editOrSaveCollection({
        id: res.id,
        categoria_id: collection.categoria_id,
        cursos: collection.cursos,
        entidades: collection.entidades,
        nome: title,
        slug: res.slug,
        autor: collection.autor,
        destaque: collection.destaque,
        cpb: collection.cpb,
        ordem: collection.ordem,
      });
      this._notificationService.success(this._translateService.instant('geral.sucesso'));
      this._storageService.store(COLLECTION_KEY, res.id);
    });
  }
}
